import './styles/tokens.css'
import './styles/global.css'
import './styles/console.css'
import { createApi, type Me } from './lib/api'
import { el, replaceChildren } from './lib/dom'
import { authErrorMessage, readAuthError, withoutAuthError } from './lib/url'
import { createHomePage } from './pages/HomePage'
import { createPageSwitcher } from './pages/pageSwitcher'
import { createReplayPage } from './pages/ReplayPage'
import { FALLBACK_PROMPT, type Page, type PageContext } from './pages/types'
import { createRouter, type Route } from './router'

const api = createApi()

const nav = (me: Me | null): HTMLElement => {
  if (!me) return el('a', { href: '/auth/login', class: 'btn' }, 'log in with GitHub')
  const logout = el(
    'button',
    {
      class: 'btn btn-quiet',
      type: 'button',
      onClick: () => void api.logout().then(() => location.assign('/')),
    },
    'logout',
  )
  return el('span', { class: 'who' }, el('span', { class: 'handle' }, `~${me.handle}`), logout)
}

/** A login failure comes back as ?auth_error=code; show it once and clean the URL. */
const authNotice = (): HTMLElement | null => {
  const code = readAuthError(location.search)
  if (!code) return null
  history.replaceState(null, '', `${location.pathname}${withoutAuthError(location.search)}`)
  return el('p', { class: 'notice is-visible notice-auth', role: 'alert' }, authErrorMessage(code))
}

const shell = (me: Me | null, outlet: HTMLElement): HTMLElement =>
  el(
    'div',
    { class: 'shell' },
    el('div', { class: 'snow', 'aria-hidden': 'true' }),
    el(
      'header',
      { class: 'topbar' },
      el('a', { href: '/', 'data-link': true, class: 'wordmark' }, 'CodeRaw'),
      el('span', { class: 'tagline' }, 'I code raw'),
      el('nav', { 'aria-label': 'account' }, nav(me)),
    ),
    el('main', { id: 'main' }, authNotice(), outlet),
    el(
      'footer',
      { class: 'foot' },
      'Season 0, the kill test. Everything you type here, including what you delete, is public.',
    ),
  )

const pageFor = (ctx: PageContext, route: Route): Page => {
  if (route.name === 'home') return createHomePage(ctx)
  if (route.name === 'replay') return createReplayPage(ctx, route.id)
  return {
    element: el('p', { class: 'empty' }, `nothing at ${route.path}. cd .. to go home.`),
    destroy: () => {},
  }
}

const boot = async (): Promise<void> => {
  const root = document.getElementById('app')
  if (!root) throw new Error('missing #app')
  const [me, prompt] = await Promise.all([
    api.me().catch(() => null),
    api.prompt().catch(() => FALLBACK_PROMPT),
  ])
  const ctx: PageContext = { api, me, prompt }
  const outlet = el('div', { class: 'outlet' })
  replaceChildren(root, [shell(me, outlet)])

  // The home page (and its draft) survives navigation; everything else is rebuilt.
  const pages = createPageSwitcher<Route>({
    create: (route) => pageFor(ctx, route),
    keepAlive: (route) => route.name === 'home',
    key: (route) => route.name,
  })
  createRouter((route) => {
    const page = pages.show(route)
    replaceChildren(outlet, [page.element])
    window.scrollTo(0, 0)
  })
}

void boot()
